import { useCallback, useEffect, useRef, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import { EnvironmentHealth, getEnvironmentHealth } from '../api/health';

/**
 * How often the diagnosis is refreshed.
 *
 * Slower than the queue counters on purpose. Those come over the hub and change by the
 * second; a diagnosis is a conclusion, and a conclusion that rewrites itself every two
 * seconds cannot be read. Ten seconds is fast enough that a fixed problem disappears while
 * you are still looking at the screen.
 */
const POLL_INTERVAL_MS = 10000;

export interface HealthMonitor {
    health: EnvironmentHealth | null;
    /** True only on the very first fetch; a refresh leaves the previous verdict standing. */
    loading: boolean;
    error: string | null;
    reload: () => void;
}

/**
 * Keeps the diagnosis of one environment up to date.
 *
 * A failed round does not clear what is on screen. The alternative - blanking the panel on
 * every hiccup - throws away a diagnosis that was correct a moment ago and replaces it with
 * nothing, which is worse than a verdict that is ten seconds old.
 */
export const useHealth = (
    customer?: string,
    environment?: string
): HealthMonitor => {
    const axios = useAxiosSwagger();

    const [health, setHealth] = useState<EnvironmentHealth | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const mounted = useRef<boolean>(true);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    const load = useCallback(async (): Promise<void> => {
        if (!axios || !customer || !environment) {
            return;
        }

        try {
            const result = await getEnvironmentHealth(
                axios,
                customer,
                environment
            );

            if (mounted.current) {
                setHealth(result);
                setError(null);
            }
        } catch (e) {
            if (mounted.current) {
                setError(describeError(e));
            }
        }
    }, [axios, customer, environment]);

    useEffect(() => {
        setHealth(null);
        setError(null);

        if (!axios || !customer || !environment) {
            return;
        }

        setLoading(true);

        void load().finally(() => {
            if (mounted.current) {
                setLoading(false);
            }
        });

        const timer = setInterval(() => void load(), POLL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [axios, customer, environment, load]);

    return { health, loading, error, reload: load };
};
